import { useEffect, useRef, useState } from "react";
import { AiOutlinePlus, AiOutlineSend } from "react-icons/ai";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import chatApi from "../../../api/chatApi";
import { Button } from "react-bootstrap";
import { IoArrowBackCircleSharp } from "react-icons/io5";

const PrivateChat = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const senderEmail = user?.userEmail;

  const location = useLocation();
  const { selectedUser } = location.state || {};
  const receiverEmail = selectedUser?.email;
  const receiverName = selectedUser?.name;

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editedMessages, setEditedMessages] = useState({});

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [refreshChat, setRefreshChat] = useState(false);

  // ... imports and state setup ...

  useEffect(() => {
    if (!senderEmail || !receiverEmail) return;

    const fetchMessages = async () => {
      try {
        const response = await chatApi.getPrivateChat({
          userEmail: senderEmail,
          reciver: receiverEmail,
          chatMode: "individual",
        });

        // Sort messages by date
        const sorted = response.data.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
        );

        // Update state
        // Note: I removed the double setMessages call you had in the original code
        setMessages(sorted);
      } catch (err) {
        console.error("Failed to fetch private messages", err);
      }
    };

    // 1. Call immediately on mount
    fetchMessages();

    // 2. Set up an interval to call it every 3 seconds (3000ms)
    const intervalId = setInterval(() => {
      fetchMessages();
    }, 3000);

    // 3. Cleanup function: Stop the interval when the user leaves the page
    return () => clearInterval(intervalId);
  }, [senderEmail, receiverEmail]); // Removed 'refreshChat' as polling covers it

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    if (editingMessage) {
      try {
        await chatApi.updateMsg(editingMessage.id, newMessage.trim());
        // setRefreshChat((prev) => !prev);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === editingMessage.id
              ? {
                  ...msg,
                  content: newMessage.trim(),
                  edited: true,
                  updatedAt: new Date().toISOString(),
                }
              : msg,
          ),
        );

        setEditedMessages((prev) => ({
          ...prev,
          [editingMessage.id]: true,
        }));
        console.log("Message edited:", {
          id: editingMessage.id,
          content: newMessage.trim(),
          sender: editingMessage.sender,
          updatedAt: new Date().toISOString(),
        });

        //setEditingMessage(null);
      } catch (err) {
        console.error("Failed to update message:", err);
      }
      setEditingMessage(null); // Reset editing state
    } else {
      //   const tempId = Date.now().toString(); // ✅ UNIQUE ID

      const messageData = {
        // id: tempId, // ✅ ADD THIS
        sender: senderEmail,
        recipients: [receiverEmail],
        content: newMessage.trim(),
        timestamp: new Date().toISOString(),
        fileUrl: selectedFile ? URL.createObjectURL(selectedFile) : null,
        chatType: "private",
        chatMode: "individual",
      };

      setMessages((prev) => [...prev, messageData]);

      try {
        await chatApi.sendMessage(messageData);
        setRefreshChat((prev) => !prev);
      } catch (err) {
        console.error("Failed to send message:", err);
      }
    }

    setNewMessage("");
    setSelectedFile(null);
  };

  const handleFileButtonClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const formatEmailToName = (email) => {
    const emailPart = email?.split("@")[0];
    return emailPart?.replace(/\d+/g, "");
  };
  const handleEdit = (msg) => {
    console.log(
      `Editing message from ${msg.sender}: "${msg.content}" (ID: ${msg.id})`,
    );

    setEditingMessage(msg);
    setNewMessage(msg.content);
  };

  const handleDelete = async (id) => {
    console.log("message deleted ", id);
    try {
      await chatApi.deleteMsg(id);
      setRefreshChat((prev) => !prev);
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => msg.id !== id),
      );

      setEditedMessages((prev) => {
        const newEdited = { ...prev };
        delete newEdited[id];
        return newEdited;
      });
    } catch (error) {
      console.error("Failed to delete message:", error);
    }
  };
  const isEditable = (timestamp) => {
    const now = new Date();
    const msgTime = new Date(timestamp);
    return (now - msgTime) / 1000 / 60 <= 5;
  };

  const handleBack = () => { 
    window.history.back();
  }
  return (
    <div
      style={{
        display: "flex",
        height: "80vh",
        border: "1px solid #ccc",
        borderRadius: "8px",
      }}
    >
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div
          style={{
            padding: "1rem",
            borderBottom: "1px solid #ddd",
            display: "flex",
            alignItems: "center",
            gap: "15px",
          }}
        >
          <h5>
            Chat with{" "}
            {receiverEmail === senderEmail
              ? "You"
              : receiverName || receiverEmail}
          </h5>
          <Button onClick={handleBack} variant="info" className="tooltip-btn">
            <IoArrowBackCircleSharp />
            <span className="tooltip-text">Go to Back</span>
          </Button>
        </div>

        {/* Messages */}
        <div
          style={{
            flex: 1,
            padding: "1rem",
            overflowY: "auto",
            backgroundColor: "#f9f9f9",
          }}
        >
          {messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                backgroundColor:
                  msg.sender === user?.userEmail ? "#e1ffc7" : "#95cc85ff",
                padding: "0.5rem",
                borderRadius: "8px",
                maxWidth: "40%",
                marginBottom: "0.5rem",
                marginLeft: msg.sender === senderEmail ? "auto" : "0",
                position: "relative",
              }}
            >
              <div>
                <strong>
                  {msg.sender === senderEmail
                    ? "You"
                    : formatEmailToName(msg.sender)}
                </strong>
              </div>

              <div style={{ paddingBottom: "1.5rem" }}>{msg.content}</div>

              <small
                style={{
                  color: "#999",
                  fontSize: "0.75rem",
                  position: "absolute",
                  bottom: "4px",
                  right: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                }}
              >
                {/* Icons LEFT side */}

                {/* Time */}
                <span>
                  {editedMessages[msg.id] && msg.updatedAt
                    ? new Date(msg.updatedAt).toLocaleTimeString()
                    : new Date(msg.timestamp).toLocaleTimeString()}
                </span>
                {editedMessages[msg.id] && (
                  <span style={{ fontStyle: "italic" }}>Edited</span>
                )}
                {msg.sender === senderEmail && (
                  <>
                    {isEditable(msg.timestamp) && (
                      <FaEdit
                        size={12}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleEdit(msg)}
                      />
                    )}
                    <FaTrash
                      size={12}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleDelete(msg.id)}
                    />
                  </>
                )}

                {/* Edited label */}
              </small>

              {msg.sender === senderEmail && (
                <div style={{ marginTop: "4px" }}>
                  {/* <FaEdit
                    size={12}
                    style={{ cursor: "pointer", marginRight: "6px" }}
                    onClick={() => {
                      setEditingMessage(msg);
                      setNewMessage(msg.content);
                    }}
                  />
                  <FaTrash
                    size={12}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleDelete(msg.id)}
                  /> */}
                </div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div
          style={{
            display: "flex",
            padding: "0.5rem 1rem",
            borderTop: "1px solid #ddd",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <button
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: "#555",
            }}
            onClick={handleFileButtonClick}
          >
            <AiOutlinePlus />
          </button>

          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <textarea
            placeholder="Type a message..."
            rows={1}
            style={{ flex: 1, resize: "none", padding: "0.5rem" }}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
          />

          <button
            onClick={handleSendMessage}
            style={{
              backgroundColor: "#007bff",
              border: "none",
              color: "white",
              padding: "0.5rem 1rem",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            <AiOutlineSend />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivateChat;
